import React, { useEffect, useState, useMemo } from 'react';
import styles from './InboxList.module.scss';
import { IconWrapper } from '../../../../Atoms/icons/Icon';
import Tooltip from '../../../../Atoms/Tooltip/Tooltip';
import {
  useFetchConversationById,
  useMarkAsRead,
} from '../../../../../Hooks/Mail';
import moment from 'moment';
import Loader from '../../../Loader/Loader';
import CheckBox from '../../../../Atoms/Inputs/CheckBox';

const InboxList = ({
  setSelectedMail,
  selectedMail,
  onDelete,
  mailInbox = [],
  isLoading,
  setRefresh,
  setSelectedItem = null,
  selected,
  isEmail, //for reusable component
  selectAllTrigger,
}) => {
  const [expandedGroups, setExpandedGroups] = useState({});
  const [hoverId, setHoverId] = useState(null);
  const [hoverFlag, setHoverFlag] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [checkedMails, setCheckedMails] = useState([]);

  const { data: conversationData, refetch } =
    useFetchConversationById(conversationId);

  const { mutate: MarkAsRead } = useMarkAsRead();

  useEffect(() => {
    if (selectedMail?.id && !selectedMail?.isRead) {
      MarkAsRead(selectedMail.id, {
        onSuccess: () => {
          setTimeout(() => {
            setRefresh?.(true);
          }, 800);
        },
      });
    }
  }, [selectedMail?.id]);

  useEffect(() => {
    const validMailIds = mailInbox.map((m) => m.id);
    setCheckedMails((prev) => prev.filter((id) => validMailIds.includes(id)));
  }, [mailInbox]);

  const getLinkedUsersLabel = (mail) => {
    if (!mail?.linkedUsers?.length) return null;

    const firstUser = mail.linkedUsers[0];
    const remainingCount = mail.linkedUsers.length - 1;
    if (firstUser?.assignedTo == null) {
      return null;
    }
    let firstName = firstUser?.assignedTo;

    firstName = firstName.slice(0, 7);

    if (remainingCount > 0) {
      return `${firstName} +${remainingCount}`;
    }

    return firstName;
  };

  const toggleGroup = (mail) => {
    const isExpanded = expandedGroups[mail.id];
    setExpandedGroups((prev) => ({
      ...prev,
      [mail.id]: !isExpanded,
    }));
    if (!isExpanded && mail.threadCount > 1) {
      setConversationId(mail.conversationId);
    }
  };

  const toggleCheckUser = (mail) => {
    const isChecked = checkedMails.includes(mail.id);
    const linkedUserIds = mail?.linkedUsers?.map((u) => u.id) || [];
    if (isChecked) {
      setCheckedMails((prev) => prev.filter((id) => id !== mail.id));

      setSelectedItem((prev) =>
        prev.filter((item) => !linkedUserIds.includes(item.id)),
      );
    } else {
      setCheckedMails((prev) => [...prev, mail.id]);

      setSelectedItem((prev) => {
        const existingIds = prev.map((item) => item.id);

        const newUsers = linkedUserIds
          .filter((id) => !existingIds.includes(id))
          .map((id) => ({ id }));

        return [...prev, ...newUsers];
      });
    }
  };

  const handleSelectAll = () => {
    const allMailIds = mailInbox.map((m) => m.id);
    const allLinkedUsers = mailInbox.flatMap(
      (mail) => mail?.linkedUsers?.map((u) => ({ id: u.id })) || [],
    );
    setCheckedMails(allMailIds);
    setSelectedItem((prev) => {
      const existingIds = prev.map((item) => item.id);

      const newUsers = allLinkedUsers.filter(
        (u) => !existingIds.includes(u.id),
      );

      return [...prev, ...newUsers];
    });
  };
  useEffect(() => {
    if (selectAllTrigger) {
      handleSelectAll();
    }
  }, [selectAllTrigger]);

  const handleUnselectAll = () => {
    const allLinkedUserIds = mailInbox.flatMap(
      (mail) => mail?.linkedUsers?.map((u) => u.id) || [],
    );
    setCheckedMails([]);
    setSelectedItem((prev) =>
      prev.filter((item) => !allLinkedUserIds.includes(item.id)),
    );
  };

  const isAllSelected =
    mailInbox.length > 0 && checkedMails.length === mailInbox.length;

  const handleDeleteMail = (mail) => {
    const linkedUserIds = mail?.linkedUsers?.map((u) => u.id) || [];

    onDelete(mail.id);

    setCheckedMails((prev) => prev.filter((id) => id !== mail.id));
    if (setSelectedItem !== null) {
      setSelectedItem((prev) =>
        prev.filter((item) => !linkedUserIds.includes(item.id)),
      );
    }
  };

  useEffect(() => {
    if (selected?.length == 0) {
      setCheckedMails([]);
    }
  }, [selected]);

  const renderEmailItem = (mail, isSubMail = false) => (
    <div
      key={mail.id}
      className={`${styles.mailItem} ${isEmail ? styles.isEmail : ''} ${!mail.isRead ? styles.unread : ''} ${
        mail.id === selectedMail?.id ? styles.active : ''
      } ${isSubMail ? styles.subMailItem : ''}`}
      onClick={() => setSelectedMail(mail)}
      onMouseEnter={() => setHoverId(mail.id)}
      onMouseLeave={() => setHoverId(null)}
    >
      <div className={styles.leftContainer}>
        {mail?.linkedUsers?.length > 0 ? (
          <div onClick={(e) => e.stopPropagation()}>
            <CheckBox
              value={checkedMails.includes(mail.id)}
              onChange={() => toggleCheckUser(mail)}
            />
          </div>
        ) : (
          <div></div>
        )}
        {(mail.threadCount > 1 || !mail.isRead) && (
          <div className={styles.left}>
            {mail.threadCount > 1 && !isSubMail && (
              <Tooltip
                content={expandedGroups[mail.id] ? 'Collapse' : 'Expand'}
              >
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleGroup(mail);
                  }}
                >
                  <IconWrapper
                    icon='arrow'
                    className={`${styles.icon} ${
                      expandedGroups[mail.id] ? styles.rotated : ''
                    }`}
                  />
                </span>
              </Tooltip>
            )}{' '}
            {!mail.isRead && (
              <Tooltip content='Unread'>
                <span className={styles.dot}></span>
              </Tooltip>
            )}
          </div>
        )}
      </div>

      <div className={styles.content}>
        <div className={styles.topRow}>
          <div className={styles.subjectRow}>
            <span className={styles.subject}>
              {mail.subject || '(no subject)'}
            </span>
          </div>

          <div className={styles.right}>
            {hoverId === mail.id ? (
              <>
                {/* <div
                  onMouseEnter={() => setHoverFlag(true)}
                  onMouseLeave={() => setHoverFlag(false)}
                >
                  <IconWrapper
                    icon={hoverFlag ? 'filledFlag' : 'flag'}
                    className={styles.starIcon}
                  />
                </div> */}

                <IconWrapper
                  icon='delete'
                  className={styles.deleteIcon}
                  color='#818181'
                  onClick={(e) => (e.stopPropagation(), handleDeleteMail(mail))}
                />
              </>
            ) : (
              <>
                {mail.isReply && (
                  <IconWrapper icon='sentArrow' className={styles.icon} />
                )}
                {mail.isSent && (
                  <span className={`${styles.linkedTag} ${styles.sent}`}>
                    Sent
                  </span>
                )}
                {mail.hasAttachments && (
                  <Tooltip content='Attachment'>
                    <IconWrapper icon='attachment' className={styles.icon} />
                  </Tooltip>
                )}
                {mail?.linkedUsers?.length > 0 &&
                  getLinkedUsersLabel(mail) !== null && (
                    <span className={styles.linkedTag}>
                      {getLinkedUsersLabel(mail)}
                    </span>
                  )}
              </>
            )}
          </div>
        </div>

        <div className={styles.from}>
          <span className={styles.snippet}>
            <span>From:</span> {mail.fromEmail}
          </span>
          <span className={styles.date}>
            {moment(mail.receivedOn).format('MMM D, YYYY hh:mm')}
          </span>
        </div>

        <p
          className={styles.snippet}
          dangerouslySetInnerHTML={{
            __html: mail.preview,
          }}
        />
      </div>
    </div>
  );

  return (
    <div className={styles.mailList}>
      {/* {mailInbox.length > 0 && (
        <div style={{ padding: 10 }}>
          <CheckBox
            value={isAllSelected}
            onChange={() =>
              isAllSelected ? handleUnselectAll() : handleSelectAll()
            }
          />
        </div>
      )} */}

      {mailInbox.length > 0 ? (
        mailInbox
          .sort((a, b) => new Date(b.receivedOn) - new Date(a.receivedOn))
          .map((mail) => (
            <div key={mail.id}>
              {renderEmailItem(mail)}

              {mail.threadCount > 1 &&
                expandedGroups[mail.id] &&
                conversationData?.data?.items?.length > 0 && (
                  <div className={styles.subGroupEmails}>
                    {conversationData.data.items
                      ?.filter((i) => i.id !== mail.id)
                      .map((subMail) => renderEmailItem(subMail, true))}
                  </div>
                )}
            </div>
          ))
      ) : (
        <p style={{ padding: 10 }}>We didn't find anything to show here</p>
      )}

      {isLoading && (
        <div className={styles.loader}>
          <Loader />
        </div>
      )}
    </div>
  );
};

export default InboxList;
